import React, { useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { getTariffs } from '../../api/promotionPackagesAPI';
import { setAdminUserSubscription } from '../../api/adminUsers';

const inputCls =
  'w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary ' +
  'placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30';

const toDateInput = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const addDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toDateInput(d);
};
const fmtMoney = (v) => `${Number(v || 0).toLocaleString('ru-RU')} ₸`;

/** Как бэкенд: individual → тарифы физлиц, остальные типы → бизнес-тарифы. */
const audienceOf = (userType) => (userType === 'individual' ? 'individual' : 'business');

/**
 * Ручная смена тарифа админом: без списания с баланса.
 * Сменить тариф (VIP-часы начисляются заново), продлить текущий или снять.
 */
const TariffDialog = ({ user, onCancel, onDone }) => {
  const current = user.subscription?.plan || null;
  const currentExpires = user.subscription?.expires_at ? toDateInput(new Date(user.subscription.expires_at)) : '';
  const [tariffs, setTariffs] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tariffId, setTariffId] = useState(current?.id ? String(current.id) : '');
  const [expires, setExpires] = useState(currentExpires);
  const [grantVip, setGrantVip] = useState(!current);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getTariffs()
      .then((d) => setTariffs(d.tariffs || []))
      .catch((err) => setLoadError(err.message || 'Не удалось загрузить тарифы'));
  }, []);

  const audience = audienceOf(user.user_type);
  const options = useMemo(
    () =>
      (tariffs || [])
        .filter((t) => !t.for_user_type || t.for_user_type === audience)
        .sort((a, b) => Number(b.is_active && !b.is_archived) - Number(a.is_active && !a.is_archived) || (a.sort_order || 0) - (b.sort_order || 0) || a.price_value - b.price_value),
    [tariffs, audience]
  );
  const selected = options.find((t) => String(t.id) === tariffId);
  const changedPlan = selected && selected.id !== current?.id;
  const vipHours = Number(selected?.conditions?.vip_hours || 0);

  const pickTariff = (id) => {
    setTariffId(id);
    setError(null);
    const t = options.find((x) => String(x.id) === id);
    if (!t) return;
    if (t.id === current?.id) {
      setExpires(currentExpires);
      setGrantVip(false);
    } else {
      setExpires(addDays(t.duration_days || 30));
      setGrantVip(true);
    }
  };

  const today = toDateInput(new Date());
  const canSave = selected && expires && expires > today && !busy && (changedPlan || expires !== currentExpires || grantVip);

  const submit = async (body, message) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await setAdminUserSubscription(user.id, body);
      onDone(updated, message);
    } catch (err) {
      setError(err.message || 'Не удалось сохранить');
      setBusy(false);
    }
  };

  const save = () =>
    submit(
      { tariff_id: selected.id, expires_at: expires, grant_vip_hours: grantVip },
      changedPlan
        ? `Тариф «${selected.name}» назначен до ${new Date(expires).toLocaleDateString('ru-RU')}`
        : `Тариф «${selected.name}» продлён до ${new Date(expires).toLocaleDateString('ru-RU')}`
    );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onCancel()}
      onKeyDown={(e) => e.key === 'Escape' && !busy && onCancel()}
    >
      <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-block border border-border-primary bg-bg-card text-left shadow-lg">
        <div className="flex items-start justify-between gap-3 border-b border-border-primary px-5 py-3.5">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-text-primary">Тариф пользователя</h3>
            <p className="truncate text-xs text-text-secondary">
              {user.name || 'Без имени'} · <span className="font-mono">#{user.id}</span> · {audience === 'individual' ? 'тарифы для физлиц' : 'бизнес-тарифы'}
            </p>
          </div>
          <button type="button" onClick={onCancel} aria-label="Закрыть" className="rounded-lg p-1 text-text-muted hover:bg-bg-tertiary">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <p className="rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-secondary">
            Сейчас:{' '}
            <b className="text-text-primary">{current ? current.name : 'Бесплатный'}</b>
            {current && currentExpires && ` до ${new Date(user.subscription.expires_at).toLocaleDateString('ru-RU')}`}
            {Number(user.subscription?.vip_hours_balance) > 0 && ` · VIP-часов: ${user.subscription.vip_hours_balance}`}
          </p>

          {loadError ? (
            <p className="text-sm text-rose-500">{loadError}</p>
          ) : (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Тариф</span>
              <select value={tariffId} onChange={(e) => pickTariff(e.target.value)} disabled={!tariffs} className={inputCls}>
                <option value="">{tariffs ? '— Выберите тариф —' : 'Загрузка…'}</option>
                {options.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} · {fmtMoney(t.price_value)} / {t.duration_days} дн.
                    {t.id === current?.id ? ' (текущий)' : ''}
                    {!t.is_active || t.is_archived ? ' — не продаётся' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          {selected && (
            <>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-text-secondary">Действует до (включительно)</span>
                <input type="date" value={expires} min={addDays(1)} onChange={(e) => setExpires(e.target.value)} className={inputCls} />
                <span className="mt-1 flex flex-wrap gap-1.5">
                  {[7, 30, 90, 365].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setExpires(addDays(d))}
                      className="rounded-md border border-border-primary px-2 py-0.5 text-[11px] text-text-secondary hover:bg-bg-tertiary"
                    >
                      +{d} дн.
                    </button>
                  ))}
                </span>
              </label>

              <label className="flex cursor-pointer items-start gap-2 text-sm text-text-primary">
                <input type="checkbox" checked={grantVip} onChange={(e) => setGrantVip(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#4F45E5]" />
                <span>
                  Начислить VIP-часы тарифа ({vipHours})
                  <span className="block text-xs text-text-muted">Текущий остаток заменится на {vipHours} ч. Для простого продления обычно не нужно.</span>
                </span>
              </label>

              <p className="flex items-start gap-2 text-xs text-text-muted">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-500" />
                Баланс пользователя не списывается. Действие записывается в журнал.
              </p>
            </>
          )}

          {error && <p className="text-sm text-rose-500">{error}</p>}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border-primary px-5 py-3">
          {current ? (
            confirmRemove ? (
              <span className="flex items-center gap-1.5">
                <span className="text-xs text-text-secondary">Снять тариф?</span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => submit({ tariff_id: null }, `Тариф снят, пользователь на бесплатном`)}
                  className="rounded-lg bg-rose-500 px-2.5 py-1 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
                >
                  Да
                </button>
                <button type="button" onClick={() => setConfirmRemove(false)} className="rounded-lg border border-border-primary px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-tertiary">
                  Нет
                </button>
              </span>
            ) : (
              <button type="button" onClick={() => setConfirmRemove(true)} className="text-sm font-medium text-rose-500 hover:underline">
                Снять тариф
              </button>
            )
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onCancel} disabled={busy} className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
              Отмена
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!canSave}
              className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
              {changedPlan ? 'Назначить' : 'Сохранить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TariffDialog;
